/**
 * EVAS2.0 報告工作站的資料存取。
 *
 * 刻意獨立成一支而不塞進 db.js：db.js 已經一千七百多行、每支函式各自 catch
 * 吞錯，而報告的讀寫**不能吞錯**——存不進去卻回成功，醫師會以為編修保住了。
 * 這裡一律讓錯誤往外拋，由呼叫端顯示。
 */

const API_URL = '/api'

async function request(url, options, { timeoutMs = 0 } = {}) {
  const controller = timeoutMs > 0 && typeof AbortController !== 'undefined'
    ? new AbortController()
    : null
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null
  let res
  try {
    res = await fetch(url, controller ? { ...options, signal: controller.signal } : options)
  } catch (err) {
    if (err && err.name === 'AbortError') {
      const timeout = new Error('讀取逾時')
      timeout.code = 'REQUEST_TIMEOUT'
      throw timeout
    }
    throw err
  } finally {
    if (timer) clearTimeout(timer)
  }
  let body = null
  try {
    body = await res.json()
  } catch (e) {
    body = null
  }
  if (!res.ok) {
    const message = (body && (body.message || body.error || body.status)) || `HTTP ${res.status}`
    const err = new Error(message)
    // 帶著 HTTP 狀態碼往上拋：呼叫端要靠它分辨「server 明確拒絕這筆語意」（4xx，
    // 重送只會拿到同一個答案）與「暫時性故障」（5xx／斷線，值得重送）。少了這個
    // 資訊，重送邏輯只能對所有失敗一視同仁，就會出現無止盡的 409 重試迴圈。
    err.status = res.status
    throw err
  }
  return body
}

/** 4xx＝用戶端語意錯誤，重送無用；其餘（5xx／fetch 拋出的網路錯誤）才可重送。 */
export function isRetryableRequestError(err) {
  const status = Number(err && err.status)
  if (!Number.isFinite(status)) return true
  return status < 400 || status >= 500
}

export const evas2ReportServices = {
  /** 音訊啟動前要求 server 由 examinations DB 正本綁定本場報告的檢查種類。 */
  bindProcedureContext({ examinationID, sessionId }) {
    const url = `${API_URL}/evas2-report/${examinationID}/procedure-context`
    return request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    }, { timeoutMs: 10000 })
  },

  /** sessionId 存在時只取該次 EOS 的結果，絕不暫時顯示同病例上一場報告。 */
  fetch(examinationID, { sessionId } = {}) {
    const session = sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : ''
    const url = `${API_URL}/evas2-report/${examinationID}?t=${Date.now()}${session}`
    return request(url, { method: 'GET' }, { timeoutMs: 10000 })
  },

  /**
   * 存醫師編修過的 Findings。
   * transcriptId 是樂觀鎖：改的必須是畫面當初載入的那一列。
   */
  saveFindings({ examinationID, transcriptId, text }) {
    const url = `${API_URL}/evas2-report/${examinationID}/findings`
    return request(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcriptId, text }),
    })
  },

  /** Findings 與 Impression 是同一份報告，以單一 request 原子儲存。 */
  saveReport({ examinationID, transcriptId, findings, impression }) {
    const url = `${API_URL}/evas2-report/${examinationID}`
    return request(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcriptId, findings, impression }),
    })
  },
}

export const evas2PacerServices = {
  fetch(examinationID) {
    return request(`${API_URL}/examinations/${examinationID}/pacer?t=${Date.now()}`, {
      method: 'GET',
    })
  },

  milestone({ examinationID, command, time, insertionLevel }) {
    return request(`${API_URL}/examinations/${examinationID}/pacer/milestone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command, time, insertionLevel }),
    })
  },

  correct({ examinationID, points, insertionLevel }) {
    return request(`${API_URL}/examinations/${examinationID}/pacer/correction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ points, insertionLevel }),
    })
  },
}

export default evas2ReportServices
